import { useEffect, useState } from 'react';
import apiClient from '@/lib/api/client';

// Viewer for MiTek SAPPHIRE / BC Framer .mmdl models: shows the plan-view image
// embedded in the container (extracted server-side via /api/mmdl/plan). Full 3D
// geometry is proprietary/undocumented, so the plan view is the honest preview.
export function MmdlPlanViewer({ url, name }: { url: string; name: string }) {
  const [imgSrc, setImgSrc] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    let objectUrl: string | null = null;
    let alive = true;
    apiClient
      .get(`/mmdl/plan?file=${encodeURIComponent(url)}`, { responseType: 'blob' })
      .then(r => {
        if (!alive) return;
        objectUrl = URL.createObjectURL(r.data as Blob);
        setImgSrc(objectUrl);
      })
      .catch(() => { if (alive) setError('No plan-view image could be extracted from this model.'); });
    return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [url]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-500">
        <span className="material-symbols-outlined text-5xl text-gray-300">deployed_code</span>
        <p className="text-sm">{error}</p>
        <p className="text-xs text-gray-400">Download the file to open it in MiTek SAPPHIRE / BC Framer.</p>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full flex flex-col">
      <div className="flex items-center gap-2 pb-2">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Plan view</span>
        <span className="text-xs text-gray-400 truncate">{name} · MiTek/BC Framer model</span>
        <div className="ml-auto flex items-center gap-1">
          <button onClick={() => setZoom(z => Math.max(0.25, z - 0.25))} className="h-7 w-7 rounded border border-gray-200 text-gray-600 hover:bg-gray-100 flex items-center justify-center" title="Zoom out">
            <span className="material-symbols-outlined text-base">remove</span>
          </button>
          <span className="text-xs text-gray-500 w-10 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom(z => Math.min(4, z + 0.25))} className="h-7 w-7 rounded border border-gray-200 text-gray-600 hover:bg-gray-100 flex items-center justify-center" title="Zoom in">
            <span className="material-symbols-outlined text-base">add</span>
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-auto rounded border border-gray-200 bg-white">
        {imgSrc ? (
          <img
            src={imgSrc}
            alt={`Plan view of ${name}`}
            style={{ width: `${zoom * 100}%`, maxWidth: 'none' }}
            className="block"
          />
        ) : (
          <div className="flex items-center justify-center h-full text-gray-400 text-sm">
            <span className="material-symbols-outlined animate-spin mr-2">progress_activity</span>
            Extracting plan view…
          </div>
        )}
      </div>
    </div>
  );
}
